/**
 * "Alert rule…" dialog (S12) — stat-tile-only in v1. Presence of an
 * `alertRule` on the tile IS the enabled state (no separate on/off toggle):
 * opening the dialog on a tile without one shows the config form with
 * sensible defaults and a "Save" that creates it; opening it on a tile that
 * already has one adds a destructive "Remove alert".
 *
 * The threshold is never judged blind (S24 review): the tile's CURRENT value
 * sits beside the input and a live line says whether the rule being edited
 * would fire right now (`utils/alertPreview.ts`, resolved exactly like
 * StatTileView resolves the number on the tile). "Send test notification"
 * (`AlertNotificationTest`) exercises the real S12 notify bridge once so the
 * OS side is verifiable before an alert ever crosses.
 *
 * The two modes (`AlertModeRadioGroup`, one explanatory sentence each) carry
 * the scheduler's background budget: the background option is disabled — with
 * the reason in its tooltip, UI Quality Contract — once the dashboard's OTHER
 * background-alert stat tiles already fill it (`canEnableBackgroundAlert`,
 * S3: maxTiles 5), since a 6th would silently never run while hidden.
 */
import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useAnalytics } from '@/core/analytics/useAnalytics';
import { canEnableBackgroundAlert } from '../services/alertEvaluator';
import { useDashboardRuntimeStore, tileRuntimeKey } from '../store/dashboardRuntimeStore';
import { alertValuePreview } from '../utils/alertPreview';
import type { Dashboard, StatTile, TileAlertRule } from '../types/dashboardTypes';
import { AlertModeRadioGroup } from './AlertModeRadioGroup';
import { AlertNotificationTest } from './AlertNotificationTest';

const OPERATORS: TileAlertRule['operator'][] = ['>', '>=', '<', '<=', '==', '!='];

type AlertRuleDialogProps = {
  dashboard: Dashboard;
  tile: StatTile | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (tile: StatTile, alertRule: TileAlertRule | undefined) => void;
};

const DEFAULT_RULE: TileAlertRule = {
  operator: '>',
  threshold: 0,
  background: false,
  cooldownSeconds: 60,
};

export function AlertRuleDialog({ dashboard, tile, open, onOpenChange, onSave }: AlertRuleDialogProps) {
  const { track } = useAnalytics();
  const [rule, setRule] = useState<TileAlertRule>(DEFAULT_RULE);
  const [notificationsSupported, setNotificationsSupported] = useState<boolean | null>(null);
  // The tile's last SUCCESSFUL run — the same source the badge/value read, so
  // the threshold can be compared against what the user is looking at.
  const lastSuccess = useDashboardRuntimeStore(
    (s) => s.byTile[tileRuntimeKey(dashboard.id, tile?.id ?? '')]?.lastSuccess,
  );

  useEffect(() => {
    if (!open || !tile) return;
    setRule(tile.alertRule ?? DEFAULT_RULE);
    track('feature:dashboard_alert_dialog_open', { hasRule: tile.alertRule !== undefined });
    void window.electronAPI?.dashboards
      ?.notificationsSupported()
      .then(setNotificationsSupported)
      .catch(() => setNotificationsSupported(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, tile?.id]);

  if (!tile) return null;

  const otherBackgroundCount = dashboard.tiles.filter(
    (t) => t.tileType === 'stat' && t.id !== tile.id && t.alertRule?.background === true,
  ).length;
  const budget = canEnableBackgroundAlert(otherBackgroundCount);

  // Current value + would-fire, resolved exactly like StatTileView resolves
  // the number on the tile itself.
  const preview = alertValuePreview(tile, lastSuccess?.result.selected, rule);

  const save = () => {
    onSave(tile, rule);
    onOpenChange(false);
  };
  const remove = () => {
    onSave(tile, undefined);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="alert-rule-dialog">
        <DialogHeader>
          <DialogTitle>Alert rule</DialogTitle>
          {/* No possessive on the quoted name — “Viewing metric”’s collided
              its closing quote with the apostrophe (S24 review). */}
          <DialogDescription>
            Notify when the value of “{tile.title}” crosses a threshold — badge in-app, and an OS
            notification when it&apos;s available.
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-4">
          <div className="flex items-center gap-2">
            <Label className="w-32 shrink-0 whitespace-nowrap text-xs text-muted-foreground">Fire when value is</Label>
            <Select value={rule.operator} onValueChange={(v: TileAlertRule['operator']) => setRule({ ...rule, operator: v })}>
              <SelectTrigger className="h-7 w-20 text-xs" data-testid="alert-rule-operator-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {OPERATORS.map((op) => (
                  <SelectItem key={op} value={op}>
                    {op}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Input
              type="number"
              className="h-7 w-28 text-xs"
              value={rule.threshold}
              onChange={(e) => setRule({ ...rule, threshold: e.target.valueAsNumber || 0 })}
              aria-label="Threshold value"
              data-testid="alert-rule-threshold-input"
            />
            {preview.text !== undefined ? (
              <span className="text-xs text-muted-foreground" data-testid="alert-rule-current-value">
                currently {preview.text}
              </span>
            ) : (
              <span className="text-xs text-muted-foreground" data-testid="alert-rule-current-value-unknown">
                no value yet
              </span>
            )}
          </div>

          {/* Live "does this rule match right now?" readout — the threshold is
              otherwise judged blind (S24 review). */}
          {preview.value !== undefined && (
            <p
              className={`-mt-2 text-[11px] ${preview.wouldFire ? 'text-warning' : 'text-muted-foreground'}`}
              aria-live="polite"
              data-testid="alert-rule-would-fire"
              data-would-fire={preview.wouldFire ? 'true' : 'false'}
            >
              {preview.wouldFire
                ? 'Would fire now — the current value already matches this rule.'
                : 'Would not fire now — the current value does not match this rule.'}
            </p>
          )}

          <AlertModeRadioGroup
            background={rule.background}
            budget={budget}
            onChange={(background) => setRule({ ...rule, background })}
          />

          <div className="flex items-center gap-2">
            <Label className="w-24 shrink-0 text-xs text-muted-foreground" htmlFor="alert-rule-cooldown-input">
              Cooldown
            </Label>
            <Input
              id="alert-rule-cooldown-input"
              type="number"
              min={0}
              className="h-7 w-24 text-xs"
              value={rule.cooldownSeconds}
              onChange={(e) => setRule({ ...rule, cooldownSeconds: Math.max(0, e.target.valueAsNumber || 0) })}
              data-testid="alert-rule-cooldown-input"
            />
            <span className="text-xs text-muted-foreground">seconds between notifications</span>
          </div>

          <AlertNotificationTest
            dashboardId={dashboard.id}
            tile={tile}
            rule={rule}
            supported={notificationsSupported}
          />

          {notificationsSupported === false && (
            <p className="text-[11px] text-muted-foreground" data-testid="alert-rule-notifications-unavailable-note">
              OS notifications aren&apos;t available on this system — the in-app badge still shows when
              this fires.
            </p>
          )}
        </div>
        <DialogFooter className="sm:justify-between">
          <div>
            {tile.alertRule && (
              <Button
                variant="ghost"
                className="text-destructive hover:text-destructive"
                onClick={remove}
                data-testid="alert-rule-remove-button"
              >
                Remove alert
              </Button>
            )}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button onClick={save} data-testid="alert-rule-save-button">
              Save
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
